/**
 * Helper pour récupérer l'email utilisateur côté client
 * 
 * Utilise une API route pour récupérer l'email de l'utilisateur connecté
 * via les cookies Azure B2C côté serveur.
 */

let cachedEmail: string | null = null
let lastFetch: number = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Récupère l'email de l'utilisateur connecté côté client
 * 
 * @returns Email de l'utilisateur ou null si non connecté
 */
export async function getClientUserEmail(): Promise<string | null> {
  // Utiliser le cache si disponible et valide
  const now = Date.now()
  if (cachedEmail && now - lastFetch < CACHE_DURATION) {
    return cachedEmail
  }

  try {
    const response = await fetch("/api/auth/user-email", {
      method: "GET",
      credentials: "include", // Important pour envoyer les cookies
    })

    if (!response.ok) {
      // 401 (Unauthorized) is normal when user is not authenticated - ignore silently
      // Only log other errors as they indicate real problems
      if (response.status !== 401) {
        console.warn(
          `[getClientUserEmail] Unexpected status ${response.status}: ${response.statusText}`
        )
      }
      cachedEmail = null
      return null
    }

    const data = await response.json()
    cachedEmail = data.email || null
    lastFetch = now

    return cachedEmail
  } catch (error) {
    // Network errors or other exceptions - log these as they indicate real problems
    // (401 status codes don't throw, they just return !response.ok)
    console.error("[getClientUserEmail] Network or other error:", error)
    cachedEmail = null
    return null
  }
}

/**
 * Vide le cache de l'email utilisateur
 */
export function clearUserEmailCache() {
  cachedEmail = null
  lastFetch = 0
}
